import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../services/api';

function AvailableLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);
  const [message, setMessage] = useState(null);
  const userRole = api.user?.role || 'salesperson';

  useEffect(() => {
    loadLeads();
  }, []);

  const loadLeads = async () => {
    try {
      const data = await api.getAvailableLeads();
      setLeads(data);
    } catch (error) {
      console.error('Failed to load leads:', error);
      setMessage({ type: 'error', text: 'Failed to load available leads' });
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async (leadId) => {
    setClaiming(leadId);
    setMessage(null);

    try {
      await api.claimLead(leadId);
      setMessage({ type: 'success', text: 'Lead claimed successfully!' });
      loadLeads();
    } catch (error) {
      console.error('Failed to claim:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to claim lead' });
    } finally {
      setClaiming(null);
    }
  };

  const getSourceBadge = (source) => {
    const colors = {
      chat: '#8b5cf6',
      manual: '#64748b',
      import: '#f59e0b',
      dms: 'var(--brand-primary, #3b82f6)',
    };
    return (
      <span style={{
        ...styles.sourceBadge,
        backgroundColor: colors[source] || '#64748b',
      }}>
        {source}
      </span>
    );
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#22c55e';
    if (score >= 50) return '#f59e0b';
    return '#ef4444';
  };

  // Priority sorting: Hot leads first, then by created_at DESC
  const sortedLeads = useMemo(() => {
    return [...leads].sort((a, b) => {
      // Hot leads first
      if (a.hot_lead && !b.hot_lead) return -1;
      if (!a.hot_lead && b.hot_lead) return 1;
      // Then by hot_score DESC if both are hot
      if (a.hot_lead && b.hot_lead) {
        const aScore = typeof a.hot_score === 'number' ? a.hot_score : -Infinity;
        const bScore = typeof b.hot_score === 'number' ? b.hot_score : -Infinity;
        if (bScore !== aScore) return bScore - aScore;
      }
      // Then by created_at DESC
      const aDate = new Date(a.created_at || 0).getTime();
      const bDate = new Date(b.created_at || 0).getTime();
      return bDate - aDate;
    });
  }, [leads]);

  if (loading) {
    return <div style={styles.loading}>Loading available leads...</div>;
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Available Leads</h1>
      <p style={styles.subtitle}>
        Hot leads can be claimed directly. Cold leads are allocated by your manager.
      </p>

      {message && (
        <div style={{
          ...styles.message,
          backgroundColor: message.type === 'success' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
          color: message.type === 'success' ? '#86efac' : '#fca5a5',
        }}>
          {message.text}
        </div>
      )}

      {sortedLeads.length === 0 ? (
        <div style={styles.emptyState}>
          
          <div style={styles.emptyText}>No available leads</div>
          <div style={styles.emptyHint}>
            All leads have been claimed. Check back soon!
          </div>
        </div>
      ) : (
        <div style={styles.leadsList}>
          {sortedLeads.map(lead => (
            <div 
              key={lead.id} 
              style={{
                ...styles.leadCard,
                border: lead.hot_lead ? '2px solid #fb7185' : '1px solid #334155',
                boxShadow: lead.hot_lead ? '0 0 10px rgba(251, 113, 133, 0.15)' : 'none',
              }}
            >
              <div style={styles.leadHeader}>
                <div style={styles.leadName}>
                  {lead.first_name} {lead.last_name}
                </div>
                <div style={styles.badgeGroup}>
                  {lead.hot_lead && (
                    <span style={styles.hotBadge}>
                      🔥 HOT
                      {typeof lead.hot_score === 'number' && ` ${lead.hot_score}`}
                    </span>
                  )}
                  {getSourceBadge(lead.source)}
                </div>
              </div>

              <div style={styles.leadDetails}>
                {lead.title && (
                  <div style={styles.detail}>
                    <span style={styles.detailIcon}>💼</span>
                    <span style={styles.detailText}>
                      {lead.title}
                      {lead.company_name && ` at ${lead.company_name}`}
                    </span>
                  </div>
                )}
                {lead.email && (
                  <div style={styles.detail}>
                    <span style={styles.detailIcon}>📧</span>
                    <a 
                      href={`mailto:${lead.email}`} 
                      style={styles.detailLink}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {lead.email}
                    </a>
                  </div>
                )}
                {lead.phone && (
                  <div style={styles.detail}>
                    <span style={styles.detailIcon}>📞</span>
                    <a 
                      href={`tel:${lead.phone}`}
                      style={styles.detailLink}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {lead.phone}
                    </a>
                  </div>
                )}
              </div>

              {(lead.qualification_score || lead.hot_score) && (
                <div style={styles.scoreSection}>
                  {lead.hot_lead && typeof lead.hot_score === 'number' ? (
                    <>
                      <span style={styles.scoreLabel}>Hot Score:</span>
                      <div style={styles.scoreBar}>
                        <div style={{
                          ...styles.scoreFill,
                          width: `${Math.min(lead.hot_score, 100)}%`,
                          backgroundColor: '#fb7185',
                        }} />
                      </div>
                      <span style={{
                        ...styles.scoreValue,
                        color: '#fb7185',
                      }}>
                        {lead.hot_score}
                      </span>
                    </>
                  ) : (
                    <>
                      <span style={styles.scoreLabel}>Score:</span>
                      <div style={styles.scoreBar}>
                        <div style={{
                          ...styles.scoreFill,
                          width: `${lead.qualification_score || 0}%`,
                          backgroundColor: getScoreColor(lead.qualification_score || 0),
                        }} />
                      </div>
                      <span style={{
                        ...styles.scoreValue,
                        color: getScoreColor(lead.qualification_score || 0),
                      }}>
                        {lead.qualification_score || 0}%
                      </span>
                    </>
                  )}
                </div>
              )}

              {(() => {
                const canClaim = lead.hot_lead || userRole !== 'salesperson';
                const isLoading = claiming === lead.id;
                return (
                  <button
                    onClick={() => canClaim ? handleClaim(lead.id) : null}
                    disabled={!canClaim || isLoading}
                    title={!canClaim ? 'Ask your manager to allocate this lead' : undefined}
                    style={{
                      ...styles.claimButton,
                      background: lead.hot_lead ? '#fb7185' : canClaim ? 'var(--brand-primary, #3b82f6)' : '#334155',
                      opacity: (!canClaim || isLoading) ? 0.5 : 1,
                      cursor: !canClaim ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {isLoading ? 'Claiming...' : lead.hot_lead ? 'Claim Hot Lead' : canClaim ? 'Claim Lead' : 'Manager Allocation Only'}
                  </button>
                );
              })()}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '800px',
    padding: '16px',
    margin: '0 auto',
  },
  title: {
    color: '#f1f5f9',
    margin: '0 0 8px 0',
    fontSize: '22px',
    fontWeight: '600',
  },
  subtitle: {
    color: '#94a3b8',
    margin: '0 0 20px 0',
    fontSize: '14px',
    lineHeight: '1.5',
  },
  message: {
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '14px',
  },
  loading: {
    color: '#94a3b8',
    fontSize: '14px',
    padding: '20px',
    textAlign: 'center',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    background: '#1e293b',
    borderRadius: '12px',
    border: '1px solid #334155',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '12px',
  },
  emptyText: {
    color: '#f1f5f9',
    fontSize: '18px',
    marginBottom: '8px',
    fontWeight: '500',
  },
  emptyHint: {
    color: '#64748b',
    fontSize: '14px',
  },
  leadsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  leadCard: {
    background: '#1e293b',
    borderRadius: '12px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  leadHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
  },
  leadName: {
    color: '#f1f5f9',
    fontSize: '18px',
    fontWeight: '600',
    flex: 1,
    wordBreak: 'break-word',
  },
  badgeGroup: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    flexShrink: 0,
  },
  hotBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '700',
    color: '#fff',
    background: '#fb7185',
    textTransform: 'uppercase',
  },
  sourceBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '600',
    color: '#fff',
    textTransform: 'uppercase',
    flexShrink: 0,
  },
  leadDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  detail: {
    color: '#94a3b8',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minHeight: '24px',
  },
  detailIcon: {
    fontSize: '14px',
    flexShrink: 0,
  },
  detailText: {
    wordBreak: 'break-word',
  },
  detailLink: {
    color: '#60a5fa',
    textDecoration: 'none',
    wordBreak: 'break-word',
  },
  scoreSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    background: '#0f172a',
    borderRadius: '8px',
  },
  scoreLabel: {
    color: '#64748b',
    fontSize: '12px',
    whiteSpace: 'nowrap',
  },
  scoreBar: {
    flex: 1,
    height: '8px',
    background: '#334155',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  scoreFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s',
  },
  scoreValue: {
    fontSize: '13px',
    fontWeight: '600',
    minWidth: '36px',
    textAlign: 'right',
  },
  claimButton: {
    width: '100%',
    padding: '14px',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    minHeight: '48px',
    touchAction: 'manipulation',
  },
};

export default AvailableLeads;
